import { useEffect, useRef, useState } from "react";
import { getAzureBlob } from "../../../services/container";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import { normalizeMaterialKey as normalizeToReferenceName } from "../../../shared/utils/materialNameNormalizer";
import { isCountableObject } from "../utils/objectCounting";
import { buildOutputSurfaceBlobUrl } from "../utils/outputSurfaceBlobUrl";

// Cap on parallel Azure blob requests: a collection cycle can span dozens of
// pictures, and firing them all at once would be heavy for both the browser
// and the backend proxy.
const FETCH_CONCURRENCY = 6;

// "waste" is a placeholder class used before a detection is classified; it
// is grouped under "unknown" everywhere else in the app (see ImageTreatment.js).
// Beyond that, raw detection classes are matched against the app-wide
// materialsDescription list (referenceName + possibleNames/synonyms) so a
// detection's crop lands under the exact same material key as the aggregate
// counts shown in the chart, instead of a near-duplicate/synonym bucket.
const normalizeMaterialKey = (cls) => {
  const lower = String(cls || "").toLowerCase();
  if (lower === "waste") return "unknown";
  return normalizeToReferenceName(lower) || lower;
};

const getCropResolution = (object) => {
  const points = object?.subregion?.points_konva || [];
  const xPoints = points.filter((_, index) => index % 2 === 0);
  const yPoints = points.filter((_, index) => index % 2 === 1);
  if (xPoints.length === 0 || yPoints.length === 0) return 0;

  const width = Math.max(...xPoints) - Math.min(...xPoints);
  const height = Math.max(...yPoints) - Math.min(...yPoints);
  return width * height;
};

const getDetectionScore = (object) =>
  Number(
    object?.subregion?.segmentation_score ?? object?.subregion?.score ?? 0,
  );

const sortByBestResolution = (objects) =>
  objects.sort((first, second) => {
    const resolutionDifference =
      getCropResolution(second) - getCropResolution(first);
    if (resolutionDifference !== 0) return resolutionDifference;
    return getDetectionScore(second) - getDetectionScore(first);
  });

const runWithConcurrency = async (items, worker, limit) => {
  const queue = [...items];
  const runnerCount = Math.min(limit, queue.length);
  const runners = Array.from({ length: runnerCount }, async () => {
    while (queue.length > 0) {
      const item = queue.shift();
      await worker(item);
    }
  });
  await Promise.all(runners);
};

const getHistoryCycleSignature = (historyCycle) =>
  (historyCycle || [])
    .map((item) => `${item._measurement}_${item._time}`)
    .join("|");

/**
 * Lazily aggregates individually detected objects (per-material crops) across
 * a whole collection cycle, so a "zoom" panel can show a scrollable grid of
 * thumbnails for a selected material. There is no dedicated backend endpoint
 * for this: it reuses the same per-image `output-surface` blob already used
 * by history export / review features, fetched once per history item and
 * indexed by material.
 *
 * Fetching only starts once (per historyCycle) and results are cached for
 * the lifetime of the hook instance: switching the selected material never
 * triggers a new fetch.
 */
export const useMaterialObjectsZoom = (historyCycle) => {
  const [objectsByMaterial, setObjectsByMaterial] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const fetchedSignatureRef = useRef(null);

  useEffect(() => {
    if (!historyCycle?.length) return;

    const signature = getHistoryCycleSignature(historyCycle);
    if (fetchedSignatureRef.current === signature) return;
    fetchedSignatureRef.current = signature;

    let isCancelled = false;
    setIsLoading(true);

    const byMaterial = {};

    const fetchOne = async (historyItem) => {
      const blobUrl = buildOutputSurfaceBlobUrl(historyItem);
      if (!blobUrl) return;
      try {
        const response = await getAzureBlob({ blobUrl });
        const segmentations = response?.data?.object_segmentations || [];
        segmentations.forEach((segmentation) => {
          if (!segmentation?.cls) return;
          if (!isCountableObject(segmentation, historyItem)) return;

          const materialKey = normalizeMaterialKey(segmentation.cls);
          if (!byMaterial[materialKey]) byMaterial[materialKey] = [];
          byMaterial[materialKey].push({
            subregion: {
              ...segmentation,
              cls: materialKey,
              color: formattedColorMaterial(materialKey),
            },
            image_url: historyItem.image_url,
          });
        });
      } catch {
        // Detection file missing/unavailable for this picture: skip it, the
        // zoom panel simply shows fewer thumbnails for the affected materials.
      }
    };

    runWithConcurrency(historyCycle, fetchOne, FETCH_CONCURRENCY).then(() => {
      if (isCancelled) return;
      Object.values(byMaterial).forEach(sortByBestResolution);
      setObjectsByMaterial(byMaterial);
      setIsLoading(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [historyCycle]);

  return { objectsByMaterial, isLoading };
};
